import { useEffect, useRef, useState } from "react";
import { submitForm } from "@/lib/api";

export default function AdvisoryConsultForm() {
  const successRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setSubmitting(true);
    setError("");
    try {
      await submitForm("advisory", form);
      setSubmitted(true);
      form.reset();
      setCharCount(0);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Unable to submit your request right now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="consult" className="scroll-mt-20 py-24 px-6 bg-white">
      <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Left - Info */}
        <div>
          <span className="inline-flex items-center gap-2 text-[#1A4D3C] text-xs font-semibold uppercase tracking-[0.25em] mb-5">
            <span className="w-8 h-px bg-[#C9A961]" />
            Request a Consultation
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F] leading-tight mb-6">
            Tell us about your goals.
          </h2>
          <p className="text-[#6B6B6B] text-base leading-relaxed mb-8">
            Whether you are ready to begin or simply want to learn more, we
            invite you to reach out. Complete the form and our team will follow
            up to discuss fit, availability, and next steps.
          </p>
          <div className="rounded-lg border border-[#E8E4DC] bg-[#F8F6F3] p-5 mb-8">
            <p className="text-[#6B6B6B] text-sm leading-relaxed">
              Consultations are introductory and education-first. Any discussion
              of family office support, private credit, or co-investment
              pathways is intended only for qualified parties where appropriate
              and remains subject to legal, regulatory, compliance, and
              diligence review.
            </p>
          </div>

          <div className="form-intro-details space-y-5 mb-10">
            {[
              {
                icon: "ri-time-line",
                title: "No Commitment Required",
                desc: "The initial consultation is simply a conversation about your goals and whether Harambee is the right fit.",
              },
              {
                icon: "ri-user-heart-line",
                title: "Personalized Approach",
                desc: "Every advisory engagement is tailored to your specific situation, goals, and long-term vision.",
              },
              {
                icon: "ri-lock-line",
                title: "Confidential & Professional",
                desc: "All conversations are handled with the highest level of discretion and professionalism.",
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#1A4D3C] text-base`} />
                </div>
                <div>
                  <div className="font-semibold text-[#2B2D2F] text-sm mb-0.5">
                    {item.title}
                  </div>
                  <div className="text-[#6B6B6B] text-xs leading-relaxed">
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <img
            loading="lazy"
            src="/assets/images/site/adv-form-001-600x380.jpg"
            alt="Harambee Global Ventures advisory consultation booking"
            className="form-intro-photo w-full h-[260px] object-cover object-top rounded-lg"
          />
        </div>

        {/* Right - Form */}
        <div className="bg-[#F8F6F3] rounded-lg p-8 md:p-10 border border-[#E8E4DC]">
          {submitted ? (
            <div
              className="text-center py-12"
              role="status"
              tabIndex={-1}
              ref={successRef}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-[#1A4D3C] text-3xl" />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#2B2D2F] mb-3">
                Inquiry Received
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed max-w-sm mx-auto">
                Thank you for reaching out to Harambee Global Ventures. Our
                advisory team will review your submission and follow up about
                availability and next steps.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#2B2D2F] mb-1">
                Consultation Inquiry
              </h3>
              <p className="text-[#6B6B6B] text-sm mb-7">
                All fields marked with * are required.
              </p>
              <p className="text-[#6B6B6B] text-xs leading-relaxed mb-6">
                By submitting this inquiry, you acknowledge that this form does
                not create an advisory-client relationship and is not an offer
                of securities, lending, or investment management services.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="AdvisoryConsultForm-components-first_name"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      First Name *
                    </label>
                    <input
                      id="AdvisoryConsultForm-components-first_name"
                      type="text"
                      name="first_name"
                      autoComplete="given-name"
                      required
                      placeholder="Your first name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="AdvisoryConsultForm-components-last_name"
                      className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                    >
                      Last Name *
                    </label>
                    <input
                      id="AdvisoryConsultForm-components-last_name"
                      type="text"
                      name="last_name"
                      autoComplete="family-name"
                      required
                      placeholder="Your last name"
                      className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-email"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    id="AdvisoryConsultForm-components-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-phone"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    id="AdvisoryConsultForm-components-phone"
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    placeholder="Include your country code"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-country"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Country of Residence *
                  </label>
                  <input
                    id="AdvisoryConsultForm-components-country"
                    type="text"
                    name="country"
                    autoComplete="country-name"
                    required
                    placeholder="e.g. United States, Kenya, United Kingdom"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-advisory_interest"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Primary Advisory Interest *
                  </label>
                  <select
                    id="AdvisoryConsultForm-components-advisory_interest"
                    name="advisory_interest"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select an area</option>
                    <option value="Cross-Border Opportunity Advisory">
                      Cross-Border Opportunity Advisory
                    </option>
                    <option value="Investor Readiness Support">
                      Investor Readiness Support
                    </option>
                    <option value="Strategic Planning">
                      Strategic Planning &amp; Relationship Development
                    </option>
                    <option value="Family Office Support">
                      Family Office Support (Planned)
                    </option>
                    <option value="Private Credit Guidance">
                      Private Credit &amp; Co-Investment (Planned)
                    </option>
                    <option value="Wealth-Building Education">
                      Wealth-Building Education
                    </option>
                    <option value="General Inquiry">
                      General Inquiry / Not Sure Yet
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-client_type"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Client Type *
                  </label>
                  <select
                    id="AdvisoryConsultForm-components-client_type"
                    name="client_type"
                    required
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select your profile</option>
                    <option value="African Diaspora Professional">
                      African / Diaspora Professional
                    </option>
                    <option value="Family / Family Office">
                      Family / Family Office
                    </option>
                    <option value="Entrepreneur">Entrepreneur</option>
                    <option value="Emerging Investor">Emerging Investor</option>
                    <option value="Experienced Investor">
                      Experienced Investor Seeking Diversification
                    </option>
                    <option value="Strategic Partner">
                      Strategic Partner / Organization
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-goals"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    Tell Us About Your Goals
                    <span className="ml-2 text-[#6B6B6B] font-normal normal-case tracking-normal">
                      ({charCount}/500)
                    </span>
                  </label>
                  <textarea
                    id="AdvisoryConsultForm-components-goals"
                    name="goals"
                    rows={4}
                    maxLength={500}
                    placeholder="What are you hoping to achieve? What prompted you to reach out to Harambee Global Ventures?"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] placeholder-[#ABABAB] focus:outline-none focus:border-[#1A4D3C] transition-colors resize-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="AdvisoryConsultForm-components-referral_source"
                    className="block text-xs font-semibold text-[#2B2D2F] uppercase tracking-wider mb-2"
                  >
                    How Did You Hear About Us?
                  </label>
                  <select
                    id="AdvisoryConsultForm-components-referral_source"
                    name="referral_source"
                    className="w-full px-4 py-3 rounded-md border border-[#E8E4DC] bg-white text-sm text-[#2B2D2F] focus:outline-none focus:border-[#1A4D3C] transition-colors cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Word of Mouth">
                      Word of Mouth / Referral
                    </option>
                    <option value="Search Engine">Search Engine</option>
                    <option value="Community Event">Community Event</option>
                    <option value="Partner Organization">
                      Partner Organization
                    </option>
                    <option value="Trading Academy">Trading Academy</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 rounded-md bg-[#1A4D3C] text-white font-semibold text-sm hover:bg-[#0D2E1E] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Consultation Request"}
                </button>
                {error && (
                  <p
                    className="text-sm text-red-700 bg-red-50 rounded-md p-3 text-center"
                    role="alert"
                  >
                    {error}
                  </p>
                )}

                <p className="text-[#6B6B6B] text-xs text-center leading-relaxed">
                  By submitting this form, you agree to be contacted by the
                  Harambee Global Ventures advisory team. Your details will be
                  sent to our team to respond to your inquiry. Please do not
                  include sensitive financial information.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
